import { isC3Experience } from "../../../shared/util/C3Utils";
define((require) => {
    "use strict";

    var EditorEvents        = require("shared/services/EditorEvents"),
        ExperienceModel     = require("shared/ui/experience/ExperienceModel"),
        LoggedInUserState   = require("shared/ui/user/LoggedInUserState"),
        Permissions         = require("shared/security/Permissions"),
        Promise             = require("shared/util/Promise"),
        PublishableMixin    = require("util/PublishableMixin"),
        PublishableState    = require("util/PublishableState"),
        RestApi             = require("shared/services/RestApi"),
        Scene               = require("shared/ui/experience/Scene"),
        Syncer              = require("shared/services/sync/Syncer"),
        SyncManager         = require("shared/services/sync/SyncManager"),
        UrlUtil             = require("shared/util/UrlUtil"),
        {COPY_CODE_BUTTON_STATE, BUTTON_STATUS} = require("shared/util/copyCode");
    const UUIDGenerator     = require("ui/experience/widgets/UUIDGenerator");

    /**
     * The values for SCENE_PLACEHOLDER_ID is fixed and is set by the API backend
     * @type {string}
     */
    const SCENE_PLACEHOLDER_ID = "111111111111111111111111";
    /**
     * The values for USER_PLACEHOLDER_ID is fixed and is set by the API backend
     * @type {string}
     */
    const USER_PLACEHOLDER_ID = "222222222222222222222222";
    /**
     * Editor experience model mixin.
     *
     * @class ExperienceModel
     * @constructor
     */
    return ExperienceModel.mixin("ExperienceModelEditorMixin", {

        defaults: {
            type:           "Experience",
            version:         null,
            createdBy:       null,
            createdAt:       0,
            lastCommentedAt: null,
            unread:          null,
            metadata:        false // set by the backend!
        },

        relations: ExperienceModel.prototype.relations,

        mixin: PublishableMixin,

        computed: {
            // Computed and transient property used for unread comment handling
            unread: {},
            cncOutput: ""
        },

        // Clear these fields on duplicate, i.e. when we duplicate to save a new copy...
        clearOnClone: function(options) {
            if (options.duplicate) {
                return ["lastCommentedAt"]; // PublishableMixin handles publish attributes
            }
        },

        // Update these fields after the specified sync method has been performed on this model; common meta data
        // fields will always be updated where applicable, e.g. "id", "version", "createdBy", etc...
        updateOnSync: function(options) {
            if (options.sync === "push") {
                this.getNativeCode("PushExperiencePostSync");
            }
            if (options.sync === "save" || (options.sync.indexOf("push") !== -1)) {
                return ["lastCommentedAt"]; // could be set silently. PushableMixin will add publish attributes
            }
            // nothing!
        },
        transient: ["scale", "cncOutput", "selectedWidgetId"],

        /**
         * Return a distinguished user-friendly name of this experience. <p>
         *
         * This will include version and variant or localisation information if present. <p>
         *
         * If no name has been set yet, the type name will be returned ("Experience").
         *
         * @return {String} The distinguished name as described; never null.
         */
        _distinguishedName: function() {
            return this.get("name") || this.__typeName;
        },

        url: function() {
            return this.isNew() ?
                RestApi.pathToCollection(RestApi.Collections.EXPERIENCES) :
                RestApi.pathToSpecificResource(RestApi.Collections.EXPERIENCES, this.get("id"));
        },

        /**
         * Pushes this experience only, not any of its variants. <p>
         *
         * This will raise an exception if this experience is currently unsaved to ensure we do not try and push
         * unsaved experiences. In addition, any server errors like conflicts (409) are also reported. <p>
         *
         * The push will raise "sync:state" during pending push requests: "sync:state" = "syncing"
         * is triggered just before the push request is sent, "sync:state" = "synced" on success, or
         * "sync:state" = "failed" on push error. <p>
         *
         * @return {Promise} The promise for the push request; never falsey.
         * @throws {Error} If this experience has unsaved changes because of an earlier save error.
         */
        push: function() {
            // Any non pushed changes to push?
            if (!this.hasNonPushedChanges()) {
                return Promise.resolved();
            }

            // Create sync options...
            var options = {
                method: "push",
                immutable: true,
                // Make sure we only push non-erroneous, non-destroyed experiences...
                validate: function() { // Invoked with this model (target) as scope
                    var error = null;

                    if (this._destroyed) {
                        error = "destroyed";
                    } else if (this.syncState.actual.state === Syncer.State.Failed) {
                        error = "unsaved";
                    }

                    if (error) {
                        return "Cannot push " + error + " experience";
                    }
                    // no error
                },
                // Make sure user can publish!
                privilege: Permissions.Privileges.PUBLISH
            };

            // Execute!
            return this._execute(options, {
                // Not really a collection on the resource, but it's modelled that way in the API
                url:  RestApi.pathToCollectionOnResource(RestApi.Collections.EXPERIENCES, this.get("id"), "push"),
                type: "PUT"
            }).promise;
        },

        getNativeCode: function(triggerOrigin) {
            // Any non pushed changes to push?
            if (!this.hasAtLeastState(PublishableState.PUSHED) ||
                !LoggedInUserState.get("integrations").isNativeContentIntegrationsEnabled()) {
                return Promise.resolved();
            }

            const url = RestApi.pathToSpecificResource(RestApi.Collections.EXPERIENCE_CODES, this.get("id"));

            const getOperationPromise = SyncManager.sync({
                method: "get",
                promise: true,
                silent: true,
                fn: {
                    url,
                    type: "GET"
                },
                resultHandler: (cncOutput) => {
                    this.set("cncOutput", cncOutput);
                }
            }).promise();

            return getOperationPromise.then(() => {
                const eventProperties = {
                    experienceId: this.get("id"),
                    experienceVersion: this.get("version"),
                    experiencePushedVersion: this.get("pushedVersion"),
                    triggerOrigin
                };

                this.set(COPY_CODE_BUTTON_STATE, BUTTON_STATUS.enabled);
                EditorEvents.trigger(EditorEvents.constants.GET_CODE, eventProperties);
            });
        },

        destroy: function(options) {
            // Make sure we have our own private copy, of other logic will affect our url. We know that Backbone.sync
            // will respect the url passed in the options, so we can generate it here, without saving the options
            // as state...
            if (options) {
                options.url = UrlUtil.appendParamObject(this.url(), options);
            }
            // Need to stop listening to events before doing the deletion, as otherwise we'll start adding
            // required scenes again...
            this.stopListening();

            // Call super to perform the destroy!
            return this.__super();
        },

        /**
         * This code checks to see if the experience is fully loaded
         * or if it contains nothing more than a placeholder scene and widgets.
         * @returns {boolean}
         */
        isExperienceShell: function() {
            return this.get("overlayScene").get("id") === SCENE_PLACEHOLDER_ID &&
            this.get("backgroundScene").get("id") === SCENE_PLACEHOLDER_ID &&
            this.get("overlayScene").get("createdBy") === USER_PLACEHOLDER_ID;
        },

        /**
         * Checks if this experience only contains immutable metadata or if the experience is empty,
         * if so, fetches the full experience model from the server.
         *
         * @return {Promise} A promise/deferred object that resolves when the data has been
         *                   fetched, and the experience no longer contains only metadata.
         */
        fetchAll: function() {
            const isImmutable = this.get("immutable");

            return  isImmutable || this.isExperienceShell() ? this.fetch() : Promise.resolved(this);
            // resolve to this instance for consistency
        },

        /**
         * Returns a display friendly version of the experience name to use in templates
         *
         * @return {String} A display friendly version of the experience name.
         */
        getDisplayName: function() {
            return this.get("name");
        },
        /**
         * Returns <tt>true</tt> if the experience is considered "empty"
         * (no changes have been made), <tt>false</tt> if not. <p>
         *
         * Duplicated experiences are not considered empty!
         *
         * @return {Boolean} <tt>true</tt> if the experience is considered "empty"
         *                  (no changes have been made), <tt>false</tt> if not.
         */
        isEmpty: function() {
            // This matches the API logic with additional support for not-persisted and clones! That is, "version"
            // defaults to null and null <= 1 is true. "version" cannot default to zero (0), as Mongo has issues with
            // zero for versioning, which the API explicitly has to handle...
            return this.get("version") <= 1 && !this.has("copyOf");
        },

        reindexScene: function() {
            this.get("scenes").each((scene, index) => {
                scene.set("sceneNumber", index + 1);
            });
        },

        /**
         * Adds a scene to the experience.
         *
         * @param {Number} [position] The position at which to add the scene. If unspecified, defaults to the end.
         * @param {Scene} [scene] The scene to add. If unspecified, a new scene is created.
         */
        addScene: function(position, scene) {
            var scenes = this.get("scenes"),
                isSceneDuplicated = Boolean(scene),
                eventName = null,
                eventOptions = null;

            if (!position || position < 1 || position > scenes.length) {
                position = scenes.length + 1;
            }

            if (!scene) {
                scene = new Scene();
            }

            scene.set({
                sceneNumber: position
            });

            // fixes scene id not being generated in c3 experiences
            // check if scene doesn't have any id associated with it 
            this.listenTo(scene, "all", function() {
                if (!scene.get("id") && isC3Experience()) {
                    scene.set({
                        id: UUIDGenerator.createUUID()
                    });
                }
            });

            scenes.add(scene, {at: position - 1});
            this.reindexScene();

            if (isSceneDuplicated) {
                eventName = EditorEvents.constants.DUPLICATE_SCENE;
                eventOptions = {triggerOrigin: "DuplicateSceneButton"};
            } else {
                eventName = EditorEvents.constants.CREATE_SCENE;
                eventOptions = {triggerOrigin: "AddNewSceneButton"};
            }
            this._triggerEditorEvent(eventName, eventOptions);
        },

        removeScene: function(scene) {
            var scenes = this.get("scenes");
            scenes.remove(scene); // TODO: why do we not call scene.destroy() for consistency? Because of undo? If so, should be called sometime, no?
            this.reindexScene();
            this._triggerEditorEvent(EditorEvents.constants.DELETE_SCENE, {
                triggerOrigin: "DeleteSceneButton"
            });
        },

        /**
         * Swaps the specified scenes in the internal scene collection.
         *
         * @param {Scene} sceneA The first scene to swap.
         * @param {Scene} sceneB The second scene to swap.
         */
        swapScenes: function(sceneA, sceneB) {
            var collection = this.get("scenes"),
                array = collection.models,
                sceneAIndex = array.indexOf(sceneA),
                sceneBIndex = array.indexOf(sceneB);

            // Swap position
            array[sceneAIndex] = array.splice(sceneBIndex, 1, array[sceneAIndex])[0];

            var sceneNumberA = sceneA.get("sceneNumber"),
                sceneNumberB = sceneB.get("sceneNumber");

            // Swap scene numbers
            sceneB.set("sceneNumber", sceneNumberA);
            sceneA.set("sceneNumber", sceneNumberB);

            var swappedScenes = [];

            swappedScenes[0] = sceneAIndex > sceneBIndex ? sceneA : sceneB;
            swappedScenes[1] = sceneAIndex < sceneBIndex ? sceneA : sceneB;

            this.trigger("scenes:swapped", {
                scenes: swappedScenes,
                a: {
                    fromSceneNumber: sceneNumberA,
                    toSceneNumber: sceneNumberB
                },
                b: {
                    fromSceneNumber: sceneNumberB,
                    toSceneNumber: sceneNumberA
                }
            });

            this._triggerEditorEvent(EditorEvents.constants.MOVE_SCENE, {
                triggerOrigin: "SwapSceneButton"
            });
        },

        /**
         * Returns <tt>true</tt> if this experience was created by the specified user.
         *
         * @param  {User} user The user.
         * @return {Boolean} <tt>true</tt> if this experience belongs to the specified user, <tt>false</tt> if not.
         */
        createdBy: function(user) {
            return this.get("createdBy") === user.get("id");
        },

        /**
         * Tracks Mix Panel events
         * @param {String} eventName The name of the event.
         * @param [Object} options A set of additional event properties to be sent.
         * @private
         */
        _triggerEditorEvent: function(eventName, options) {
            var eventProperties = {
                experienceId: this.get("id"),
                experienceVersion: this.get("version"),
                experiencePushedVersion: this.get("pushedVersion"),
                triggerOrigin: options.triggerOrigin
            };

            EditorEvents.trigger(eventName, eventProperties);
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/ExperienceModelEditorMixin.js